import { Button, Stack } from '@mui/material'
import { useWindowHeight } from '@react-hook/window-size'
import React, { useCallback, useEffect, useState } from 'react'
import { Fade, IconButton, InformationModal } from 'utoniq-ui'

import { ItemNftMintStatus } from '@/clients/utoniq-core/schema'
import { BaseLayout, Head } from '@/components/layouts'
import { useBrowserLink } from '@/components/lib/BrowserLink'
import { ThumbnailFullScreenModal } from '@/components/organisms/item'
import { ArtistThemeProvider } from '@/components/styles'
import { useRouter, useTranslation } from '@/core'
import { Organism, Template } from '@/core/architecture'
import { getMagazineHistory } from '@/core/native/storage'
import { getHelpUrl } from '@/lib/helpUrl'

import { NftIssuingModal } from './components/NftIssuingModal'
import {
  OwnedItemDetailAudioView,
  OwnedItemDetailAvatarView,
  OwnedItemDetailMagazineView,
  OwnedItemDetailMerchView,
  OwnedItemDetailTicketView,
  OwnedItemDetailVideoView,
  OwnedItemDetailWallpaperView,
} from './components/resourceView'
import { MagazinePlayerModal } from './components/resourceView/OwnedItemDetailMagazineView'
import { SkeletonLoader } from './components/SkeletonLoader'

export * as fragments from './fragments'

import type { Action, State } from '../viewModel'

export const OwnedItemDetailTemplate: Template<
  State,
  Action & {
    onClose?: () => void
  }
> = ({
  state,
  onSettingAvatar,
  onSettingWallpaper,
  onProtectUserItem,
  onClickShare,
  onClose,
  onUseTicket,
  onPurchaseCharge,
  onViewCollectionMagazine,
  onBuyItem,
  onImageDownload,
  onGenerateWallpaperPrintQrcode,
}) => {
  const { router, routerNames, routerPages } = useRouter()
  const windowHeight = useWindowHeight()
  const { onClick } = useBrowserLink(getHelpUrl('ownedItemDetail'))
  // マガジンアイテムの表示に関する状態
  const [openMagazineModal, setOpenMagazineModal] = useState(false)

  return (
    <ArtistThemeProvider artistId={state.portfolio?.item?.issuedArtist?.id}>
      <BaseLayout
        safeArea={openMagazineModal ? { pt: 0, pb: 0 } : undefined}
        header={
          openMagazineModal
            ? undefined
            : {
                left: (
                  <IconButton.Close
                    edge="start"
                    size="medium"
                    onClick={
                      onClose
                        ? onClose
                        : () => router.goBack(routerPages.MyOwnedItems)
                    }
                  />
                ),
                right: (
                  <Stack direction="row" spacing={3}>
                    <IconButton.Share
                      onClick={onClickShare}
                      edge="end"
                      color="primary.main"
                      size="small"
                    />
                    <IconButton.Help
                      edge="end"
                      color="primary.main"
                      size="small"
                      onClick={onClick}
                    />
                  </Stack>
                ),
              }
        }
        sx={{ minHeight: 'unset', height: windowHeight }}
      >
        <Head
          title={state.portfolio?.item?.title || routerNames.OwnedItemDetail}
        />

        <Fade style={{ height: '100%' }}>
          <ItemDetailBody
            loggedUser={state.loggedUser}
            loading={state.loading}
            QRCodePrinting={state.QRCodePrinting}
            loadingOnce={state.loadingOnce}
            purchasingItem={state.purchasingItem}
            portfolio={state.portfolio}
            user={state.user}
            customProfileUpdating={state.customProfileUpdating}
            onSettingAvatar={onSettingAvatar}
            onSettingWallpaper={onSettingWallpaper}
            onProtectUserItem={onProtectUserItem}
            protectingItem={state.protectingItem}
            onUseTicket={onUseTicket}
            onPurchaseCharge={onPurchaseCharge}
            purchasablePrepaidPoints={state.purchasablePrepaidPoints}
            paymentSource={state.paymentSource}
            point={state.point}
            chargeLoading={state.chargeLoading}
            onClickShare={onClickShare}
            onViewCollectionMagazine={onViewCollectionMagazine}
            onBuyItem={onBuyItem}
            onImageDownload={onImageDownload}
            openMagazineModal={openMagazineModal}
            setOpenMagazineModal={setOpenMagazineModal}
            onGenerateWallpaperPrintQrcode={onGenerateWallpaperPrintQrcode}
            enablePointCarrierPayment={state.enablePointCarrierPayment}
          />
        </Fade>
      </BaseLayout>
    </ArtistThemeProvider>
  )
}

const ItemDetailBody: Organism<
  State &
    Action & {
      openMagazineModal: boolean
      setOpenMagazineModal: (val: boolean) => void
    }
> = ({
  loading,
  QRCodePrinting,
  loadingOnce,
  purchasingItem,
  portfolio,
  user,
  loggedUser,
  customProfileUpdating,
  onSettingAvatar,
  onSettingWallpaper,
  protectingItem,
  onProtectUserItem,
  onUseTicket,
  paymentSource,
  onPurchaseCharge,
  purchasablePrepaidPoints,
  point,
  chargeLoading,
  onClickShare,
  onViewCollectionMagazine,
  onBuyItem,
  onImageDownload,
  openMagazineModal,
  setOpenMagazineModal,
  onGenerateWallpaperPrintQrcode,
  enablePointCarrierPayment,
}) => {
  const { t } = useTranslation('ownedItemDetail')
  const { router, routerPages, generatePath } = useRouter<{ itemId: string }>()

  /** アイテム画像を全画面表示するモーダル https://zpl.io/bPyED5M */
  const [openFullScreenModal, setOpenFullScreenModal] = useState(false)
  const [showMultipleImage, setShowMultipleImage] = useState(false)
  const handleExtension = () => setOpenFullScreenModal(true)

  // カードの表裏に関する状態
  const [isFlipped, setIsFlipped] = useState(false)

  const handleReverseThumbnail = useCallback(() => {
    setIsFlipped(prev => !prev)
  }, [setIsFlipped])

  /** プロテクト設定時に表示する確認モーダルの状態 https://zpl.io/jpj6lPW */
  const [openProtectModal, setOpenProtectModal] = useState(false)
  const handleProtect = () => {
    if (portfolio?.isUserProtected) {
      onProtectUserItem()
      return
    }
    setOpenProtectModal(true)
  }

  /** NFT発行時のモーダル状態 https://zpl.io/B1oAjEk */
  const [openNftIssuingModal, setOpenNftIssuingModal] = useState(false)

  useEffect(() => {
    // マガジンの最新の閲覧履歴とアイテムIDが一致する場合、ビュワーを開く
    ;(async () => {
      const { itemId, open } = await getMagazineHistory()
      if (router.query.itemId === itemId) {
        setOpenMagazineModal(open)
      }
    })()

    if (
      portfolio?.serials?.some(
        serial => serial?.nftToken?.status === ItemNftMintStatus.Processing
      )
    )
      setOpenNftIssuingModal(true)
  }, [portfolio])

  if (openMagazineModal)
    return (
      <MagazinePlayerModal
        loading={loadingOnce}
        portfolio={portfolio}
        open={openMagazineModal}
        onClose={() => {
          setOpenMagazineModal(false)
        }}
        onClickShare={onClickShare}
        paymentSource={paymentSource}
        purchasablePrepaidPoints={purchasablePrepaidPoints}
        onPurchaseCharge={(plan, paymentMethod) =>
          onPurchaseCharge(plan, paymentMethod, {
            // 外部キャリア決済画面での処理が成功した場合のリダイレクト先
            successUrl: `${window.location.origin}${generatePath(
              routerPages.OwnedItemDetail,
              {
                itemId: router.query.itemId,
              }
            )}?points=${plan.points}`,
            // 外部キャリア決済画面での処理がキャンセルされた場合のリダイレクト先
            cancelUrl: `${window.location.origin}${generatePath(
              routerPages.OwnedItemDetail,
              {
                itemId: router.query.itemId,
              }
            )}`,
            // 外部キャリア決済画面での処理がエラーになった場合のリダイレクト先
            errorUrl: `${window.location.origin}${generatePath(
              routerPages.OwnedItemDetail,
              {
                itemId: router.query.itemId,
              }
            )}`,
          })
        }
        point={point}
        purchasingItem={!!purchasingItem}
        chargeLoading={chargeLoading}
        onBuyItem={onBuyItem}
        enablePointCarrierPayment={enablePointCarrierPayment}
      />
    )

  return (
    <>
      {/* アイテムデータのコンポーネント */}
      <ItemTypeSwitch
        loading={loading}
        QRCodePrinting={QRCodePrinting}
        portfolio={portfolio}
        loggedUser={loggedUser}
        user={user}
        customProfileUpdating={customProfileUpdating}
        onSetWallpaper={onSettingWallpaper}
        onSetAvatar={onSettingAvatar}
        onClickProtectUserItem={handleProtect}
        onUseTicket={onUseTicket}
        onExtend={handleExtension}
        onSetOpenMagazineModal={setOpenMagazineModal}
        isFlipped={isFlipped}
        onReverseThumbnail={handleReverseThumbnail}
        onViewCollectionMagazine={onViewCollectionMagazine}
        onOpenFullScreen={() => setOpenFullScreenModal(true)}
        onOpenMultipleImage={() => {
          setOpenFullScreenModal(true)
          setShowMultipleImage(true)
        }}
        onImageDownload={onImageDownload}
        onGenerateWallpaperPrintQrcode={onGenerateWallpaperPrintQrcode}
      />
      {/* 全画面プレビュー */}
      <ThumbnailFullScreenModal
        isDrm={portfolio?.item?.isDrm}
        open={openFullScreenModal}
        onClose={() => {
          setOpenFullScreenModal(false)
          setShowMultipleImage(false)
        }}
        thumbnail={portfolio?.item?.thumbnail}
        images={portfolio?.item?.images}
        showMultipleImage={showMultipleImage}
        isFlipped={isFlipped}
      />
      {/* NFT発行時に出るモーダル */}
      <NftIssuingModal
        open={openNftIssuingModal}
        onClose={() => setOpenNftIssuingModal(false)}
      />
      {/* プロテクト設定時に出るモーダル */}
      <InformationModal
        open={openProtectModal}
        onClose={() => setOpenProtectModal(false)}
        title={t('protectedModalTitle', 'プロテクトに設定します。')}
        content={t(
          'protectedModalContent',
          'トレード申請中のアイテムの場合、トレード申請が自動的にキャンセルされます。'
        )}
        buttons={
          <Button
            size="large"
            loading={protectingItem}
            disabled={protectingItem}
            sx={{ minWidth: 200 }}
            onClick={() =>
              onProtectUserItem().finally(() => setOpenProtectModal(false))
            }
          >
            {t('ok')}
          </Button>
        }
        cancelText={t('close')}
      />
    </>
  )
}

const ItemTypeSwitch: Organism<{
  loading: boolean
  QRCodePrinting: boolean
  loggedUser: State['loggedUser']
  portfolio: State['portfolio']
  user: State['user']
  customProfileUpdating: boolean
  isFlipped: boolean
  onSetWallpaper: () => Promise<boolean>
  onSetAvatar: () => Promise<void>
  onReverseThumbnail: () => void
  onSetOpenMagazineModal: (val: boolean) => void
  onExtend: () => void
  onClickProtectUserItem: () => void
  onUseTicket: Action['onUseTicket']
  onViewCollectionMagazine: Action['onViewCollectionMagazine']
  onOpenFullScreen: () => void
  onOpenMultipleImage: () => void
  onImageDownload: Action['onImageDownload']
  onGenerateWallpaperPrintQrcode: Action['onGenerateWallpaperPrintQrcode']
}> = ({
  loading,
  QRCodePrinting,
  portfolio,
  loggedUser,
  user,
  customProfileUpdating,
  isFlipped,
  onSetWallpaper,
  onExtend,
  onSetAvatar,
  onClickProtectUserItem,
  onUseTicket,
  onReverseThumbnail,
  onSetOpenMagazineModal,
  onViewCollectionMagazine,
  onOpenFullScreen,
  onOpenMultipleImage,
  onImageDownload,
  onGenerateWallpaperPrintQrcode,
}) => {
  switch (portfolio?.item?.type) {
    case 'audio':
      return (
        <OwnedItemDetailAudioView
          loading={loading}
          portfolio={portfolio}
          isFlipped={isFlipped}
          onReverseThumbnail={onReverseThumbnail}
          onClickProtectUserItem={onClickProtectUserItem}
          onOpenFullScreen={onOpenFullScreen}
          onOpenMultipleImage={onOpenMultipleImage}
        />
      )
    case 'video':
      return (
        <OwnedItemDetailVideoView
          loading={loading}
          portfolio={portfolio}
          isFlipped={isFlipped}
          onReverseThumbnail={onReverseThumbnail}
          onClickProtectUserItem={onClickProtectUserItem}
          onOpenFullScreen={onOpenFullScreen}
          onOpenMultipleImage={onOpenMultipleImage}
        />
      )
    case 'magazine':
      return (
        <OwnedItemDetailMagazineView
          loading={loading}
          portfolio={portfolio}
          isFlipped={isFlipped}
          onExpand={onExtend}
          onReverseThumbnail={onReverseThumbnail}
          onClickProtectUserItem={onClickProtectUserItem}
          onSetOpenMagazineModal={onSetOpenMagazineModal}
          onViewCollectionMagazine={onViewCollectionMagazine}
          onOpenFullScreen={onOpenFullScreen}
          onOpenMultipleImage={onOpenMultipleImage}
        />
      )
    case 'avatar':
      return (
        <OwnedItemDetailAvatarView
          loading={loading}
          portfolio={portfolio}
          isFlipped={isFlipped}
          onReverseThumbnail={onReverseThumbnail}
          onSetAvatar={onSetAvatar}
          onClickProtectUserItem={onClickProtectUserItem}
          onOpenFullScreen={onOpenFullScreen}
          onOpenMultipleImage={onOpenMultipleImage}
        />
      )
    case 'wallpaper':
      return (
        <OwnedItemDetailWallpaperView
          loading={loading}
          QRCodePrinting={QRCodePrinting}
          portfolio={portfolio}
          user={user}
          customProfileUpdating={customProfileUpdating}
          isFlipped={isFlipped}
          onReverseThumbnail={onReverseThumbnail}
          onSetWallpaper={onSetWallpaper}
          onClickProtectUserItem={onClickProtectUserItem}
          onOpenFullScreen={onOpenFullScreen}
          onOpenMultipleImage={onOpenMultipleImage}
          onImageDownload={onImageDownload}
          onGenerateWallpaperPrintQrcode={onGenerateWallpaperPrintQrcode}
        />
      )
    case 'merch':
      return (
        <OwnedItemDetailMerchView
          loading={loading}
          portfolio={portfolio}
          isFlipped={isFlipped}
          onReverseThumbnail={onReverseThumbnail}
          onClickProtectUserItem={onClickProtectUserItem}
          onOpenFullScreen={onOpenFullScreen}
          onOpenMultipleImage={onOpenMultipleImage}
        />
      )
    case 'ticket':
      return (
        <OwnedItemDetailTicketView
          loading={loading}
          portfolio={portfolio}
          isFlipped={isFlipped}
          loggedUser={loggedUser}
          onReverseThumbnail={onReverseThumbnail}
          onClickProtectUserItem={onClickProtectUserItem}
          onUseTicket={onUseTicket}
          onOpenFullScreen={onOpenFullScreen}
          onOpenMultipleImage={onOpenMultipleImage}
        />
      )
    default:
      return <SkeletonLoader />
  }
}
